import type { ManifestMap } from '@xing-com/crate-core-assets';
import { joinUrl } from '@xing-com/crate-core-assets';

import { KNOWN_SITES, type Site } from './detect-site';

export const CATCH_ALL_ROUTE = '/*splat';

type RouteInfo = {
  plug: string;
  path: string;
  outlet?: string | undefined;
  owner: string;
  xinglet: string;
};

function extractRouteInfos(manifestMap: ManifestMap): RouteInfo[] {
  const regExp = /^(?:@([\w-]+))?([/\w-:]*?)(?:\/@([\w-]+)|(\/\*))?$/;

  const infos = Object.entries(manifestMap).flatMap(([xinglet, manifest]) => {
    const { contributes = {}, owner = 'unknown' } = manifest.metadata;
    const { route, routes } = contributes;

    return [...(route ? [route] : []), ...(routes ?? [])].map(
      (value): RouteInfo => {
        const matchData = value.match(regExp);
        if (!matchData) throw new Error(`Invalid route path: ${value}`);
        const [, plug = 'xing', _path, outlet, catchAll] = matchData;

        let path = _path;
        if (!path.startsWith('/')) path = `/${path}`;
        if (catchAll) path = `${path}${CATCH_ALL_ROUTE}`;

        return { plug, path, outlet, owner, xinglet };
      }
    );
  });

  return infos;
}

type RouteNode = { info: RouteInfo; nodes: RouteNode[] };

function buildRoutesGraph(infos: RouteInfo[]): Map<string, RouteNode[]> {
  const outletToPlugsMap = new Map<string, RouteNode[]>();

  const getOrInsert = (outlet: string): RouteNode[] => {
    const plugs = outletToPlugsMap.get(outlet) ?? [];
    outletToPlugsMap.set(outlet, plugs);
    return plugs;
  };

  for (const info of infos) {
    const nodes = info.outlet ? getOrInsert(info.outlet) : [];
    getOrInsert(info.plug).push({ info, nodes });
  }

  return outletToPlugsMap;
}

export type Route = {
  site: Site;
  path: string;
  owner: string;
  xinglet: string;
  parents: string[];
};

function extractRoutesFromGraph(parent: Route, nodes: RouteNode[]): Route[] {
  if (nodes.length === 0) return [parent];

  return nodes.flatMap(({ info, nodes: children }) => {
    const { owner, xinglet } = info;

    const parents = [...parent.parents, parent.xinglet];
    if (parents.includes(xinglet)) {
      throw new Error(
        `Circular outlets detected in routes: ${parents.join(' -> ')} -> ${xinglet}`
      );
    }

    const path = joinUrl(parent.path, info.path);
    const site = parent.site;
    const route: Route = { site, path, owner, xinglet, parents };

    return extractRoutesFromGraph(route, children);
  });
}

function sortRoutes(routes: Route[]): Route[] {
  // A negative value indicates that a should come before b.
  // A positive value indicates that a should come after b.
  return routes.toSorted((a, b) => {
    const partsA = a.path.split('/').slice(1);
    const partsB = b.path.split('/').slice(1);
    const max = Math.max(partsA.length, partsB.length);

    for (let i = 0; i < max; i++) {
      const partA = partsA[i];
      const partB = partsB[i];

      if (partA === partB) continue;

      // `undefined` comes after not `undefined`
      if (partA === undefined && partB !== undefined) return +1;
      if (partA !== undefined && partB === undefined) return -1;

      // '' comes after not ''
      if (partA === '' && partB !== '') return +1;
      if (partA !== '' && partB === '') return -1;

      const charA = partA[0];
      const charB = partB[0];

      // '*' comes after not '*'
      if (charA === '*' && charB !== '*') return +1;
      if (charA !== '*' && charB === '*') return -1;

      // ':' comes after not ':'
      if (charA === ':' && charB !== ':') return +1;
      if (charA !== ':' && charB === ':') return -1;

      return partA.localeCompare(partB);
    }

    return a.path.localeCompare(b.path);
  });
}

export function extractRoutes(
  manifestMap: ManifestMap,
  filterBySite?: string
): Route[] {
  const infos = extractRouteInfos(manifestMap);
  const nodes = buildRoutesGraph(infos);

  const routes = KNOWN_SITES.flatMap((site) => {
    if (filterBySite && site !== filterBySite) return [];
    const siteNodes = nodes.get(site) ?? [];
    return siteNodes.flatMap(({ info, nodes: children }) => {
      const { path, owner, xinglet } = info;
      const route: Route = { site, path, owner, xinglet, parents: [] };

      return extractRoutesFromGraph(route, children);
    });
  });

  return sortRoutes(routes);
}
